/**
 * Collections Filter Web Component
 * Handles tab-based filtering of collection groups
 */
if (!customElements.get('collections-filter')) {
    class CollectionsFilter extends HTMLElement {
        constructor() {
            super();
            this.tabs = this.querySelectorAll('[data-filter-tab]');
            this.groups = this.querySelectorAll('[data-filter-group]');
            this.allButton = this.querySelector('[data-filter-all]');
            this.tabsWrapper = this.querySelector('.collections-filtered__tabs-wrapper');
            this.init();
        }

        init() {
            // Bind click events to tabs
            this.tabs.forEach((tab) => {
                tab.addEventListener('click', this.handleTabClick.bind(this));
            });

            // Bind click event to "All" button if exists
            if (this.allButton) {
                this.allButton.addEventListener('click', this.showAll.bind(this));
            }

            // Set initial active state (first tab or all)
            const activeTab = this.querySelector('[data-filter-tab][data-active="true"]');
            if (!activeTab && this.tabs.length > 0) {
                // If no active tab and "all" button exists with active state, show all
                if (this.allButton && this.allButton.dataset.active === 'true') {
                    this.showAll();
                } else if (this.tabs.length > 0) {
                    // Otherwise activate first tab
                    this.filterByTab(this.tabs[0], false);
                }
            }
        }

        /**
         * Check if tabs wrapper is currently in "stuck" sticky mode
         * (i.e., it has scrolled past its natural position and is now fixed)
         */
        isTabsSticky() {
            if (!this.tabsWrapper) return false;

            const tabsStyle = getComputedStyle(this.tabsWrapper);
            if (tabsStyle.position !== 'sticky') return false;

            const styles = getComputedStyle(document.documentElement);
            const announcementBarHeight = parseInt(styles.getPropertyValue('--announcement-bar-height') || '0', 10);
            const headerHeight = parseInt(styles.getPropertyValue('--header-height') || '0', 10);

            // Get current position of tabs wrapper
            const tabsRect = this.tabsWrapper.getBoundingClientRect();

            // If the tabs top position is at or near the sticky position, it's "stuck"
            // We use a small threshold (2px) to account for rounding
            const stickyTop = announcementBarHeight + headerHeight;
            return tabsRect.top <= stickyTop + 2;
        }

    /**
     * Get the height of sticky elements (announcement bar + header + tabs) for scroll offset calculation
     * Uses CSS variables set by the sticky-header component
     */
    getStickyOffset() {
      const styles = getComputedStyle(document.documentElement);
      const announcementBarHeight = parseInt(styles.getPropertyValue('--announcement-bar-height') || '0', 10);
      const headerHeight = parseInt(styles.getPropertyValue('--header-height') || '0', 10);
      const tabsHeight = this.tabsWrapper ? this.tabsWrapper.offsetHeight : 0;

      return announcementBarHeight + headerHeight + tabsHeight;
    }

        /**
         * Scroll to target group with offset for sticky elements
         * @param {HTMLElement} group - The group element to scroll to
         * @param {boolean} wasSticky - Whether tabs were sticky before content change
         */
        scrollToGroup(group, wasSticky = false) {
            if (!group) return;

            // Only scroll if tabs were sticky before the content change
            if (!wasSticky) return;

            // Calculate the absolute position where we want to scroll
            const stickyOffset = this.getStickyOffset();
            const groupOffsetTop = group.getBoundingClientRect().top + window.scrollY;
            const targetScrollPosition = groupOffsetTop - stickyOffset;

            window.scrollTo({
                top: targetScrollPosition,
                behavior: 'smooth'
            });
        }

        handleTabClick(event) {
            event.preventDefault();
            const tab = event.currentTarget;
            this.filterByTab(tab, true);
        }

        filterByTab(tab, shouldScroll = true) {
            const filterValue = tab.dataset.filterTab;

            // IMPORTANT: Capture sticky state BEFORE modifying the DOM
            // This ensures we know if we need to scroll even after content height changes
            const wasSticky = this.isTabsSticky();

            // Update tab active states
            this.tabs.forEach((t) => {
                t.dataset.active = 'false';
                t.classList.remove('collections-filtered__tab--active');
            });
            tab.dataset.active = 'true';
            tab.classList.add('collections-filtered__tab--active');

            // Update all button state
            if (this.allButton) {
                this.allButton.dataset.active = 'false';
                this.allButton.classList.remove('collections-filtered__tab--active');
            }

            // Show/hide groups and find the target group
            let targetGroup = null;
            this.groups.forEach((group) => {
                if (group.dataset.filterGroup === filterValue) {
                    group.hidden = false;
                    targetGroup = group;
                } else {
                    group.hidden = true;
                }
            });

            // Scroll to the target group after DOM update
            // Use double requestAnimationFrame to ensure DOM has recalculated layout
            if (shouldScroll && targetGroup) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        this.scrollToGroup(targetGroup, wasSticky);
                    });
                });
            }
        }

        showAll(event) {
            if (event) event.preventDefault();

            // IMPORTANT: Capture sticky state BEFORE modifying the DOM
            const wasSticky = this.isTabsSticky();

            // Update tab states
            this.tabs.forEach((t) => {
                t.dataset.active = 'false';
                t.classList.remove('collections-filtered__tab--active');
            });

            // Update all button state
            if (this.allButton) {
                this.allButton.dataset.active = 'true';
                this.allButton.classList.add('collections-filtered__tab--active');
            }

            // Show all groups
            this.groups.forEach((group) => {
                group.hidden = false;
            });

            // Scroll to first group when showing all
            if (event && this.groups.length > 0) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        this.scrollToGroup(this.groups[0], wasSticky);
                    });
                });
            }
        }
    }

    customElements.define('collections-filter', CollectionsFilter);
}

/**
 * Extra description modal handler
 * Handles opening and closing of extra description modals via event delegation
 */
(function() {
    function handleExtraDescriptionModal(e) {
        // Handle trigger button click
        const trigger = e.target.closest('[data-extra-description-trigger]');
        if (trigger) {
            const blockId = trigger.dataset.extraDescriptionTrigger;
            const dialog = document.querySelector(`[data-extra-description-dialog="${blockId}"]`);
            if (dialog) {
                dialog.showModal();
            }
            return;
        }

        // Handle close button click
        const closeBtn = e.target.closest('[data-extra-description-close]');
        if (closeBtn) {
            const dialog = closeBtn.closest('dialog');
            if (dialog) {
                dialog.close();
            }
            return;
        }

        // Handle backdrop click (close dialog)
        if (e.target.tagName === 'DIALOG' && e.target.hasAttribute('data-extra-description-dialog')) {
            e.target.close();
        }
    }

    // Use event delegation on document for dynamic content
    document.addEventListener('click', handleExtraDescriptionModal);

    // Handle ESC key for dialog closing (native browser behavior, but ensure it works)
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            const openDialog = document.querySelector('dialog[data-extra-description-dialog][open]');
            if (openDialog) {
                openDialog.close();
            }
        }
    });

    // First-add interception: show modal on first "add" click per group and button type
    // Uses capture phase to intercept before smart-buy-button handler
    // Supports separate triggers for subscription (SUB) and onetime (ONE) buttons
    const shownGroupsSub = new Set();
    const shownGroupsOne = new Set();

    document.addEventListener('click', (e) => {
        const addBtn = e.target.closest('[data-smart-buy-action="add"]');
        if (!addBtn) return;

        const group = addBtn.closest('[data-filter-group]');
        if (!group) return;

        // Find the smart-buy-button parent to determine button type
        const smartBuyButton = addBtn.closest('smart-buy-button');
        if (!smartBuyButton) return;

        const isSubscription = smartBuyButton.dataset.mode === 'bundle';
        const groupId = group.dataset.filterGroup;
        let shouldShowModal = false;
        let shownGroups = null;

        if (isSubscription) {
            // Subscription button: check data-show-modal-on-add-sub
            if (group.dataset.showModalOnAddSub !== 'true') return;
            if (shownGroupsSub.has(groupId)) return;
            shownGroups = shownGroupsSub;
            shouldShowModal = true;
        } else {
            // Onetime button: check data-show-modal-on-add-one
            if (group.dataset.showModalOnAddOne !== 'true') return;
            if (shownGroupsOne.has(groupId)) return;
            shownGroups = shownGroupsOne;
            shouldShowModal = true;
        }

        if (!shouldShowModal) return;

        const dialog = group.querySelector(`[data-extra-description-dialog="${groupId}"]`);
        if (!dialog) return;

        // Mark group as shown for this button type and display modal
        // Don't preventDefault - let smart-buy-button handle the add action normally
        shownGroups.add(groupId);
        dialog.showModal();
    }, true); // Capture phase - executes before smart-buy-button handler
})();

